import { PurchaseVatLine } from "types/InvoiceInbox";

// The Lithuanian VMI VAT classifier codes (PVM1…PVM100) an accountant can put
// on a line of a RECEIVED invoice, in the order the picker lists them: domestic
// rates first, then EU acquisitions, services from abroad, foreign suppliers
// registered here, the margin scheme and the rare cases. `rate` is the VAT
// rate the code implies, null where the code is not tied to one rate.
export const PVM_CODE_OPTIONS: { code: string; rate: number | null }[] = [
  { code: "PVM1", rate: 21 },
  { code: "PVM2", rate: 9 },
  { code: "PVM3", rate: 5 },
  { code: "PVM58", rate: 12 },
  { code: "PVM5", rate: null },
  { code: "PVM14", rate: 0 },
  { code: "PVM25", rate: 21 },
  { code: "PVM26", rate: 9 },
  { code: "PVM27", rate: 5 },
  { code: "PVM16", rate: 21 },
  { code: "PVM17", rate: 9 },
  { code: "PVM18", rate: 5 },
  { code: "PVM35", rate: 0 },
  { code: "PVM36", rate: null },
  { code: "PVM19", rate: null },
  { code: "PVM21", rate: 21 },
  { code: "PVM40", rate: 5 },
  { code: "PVM57", rate: 9 },
  { code: "PVM41", rate: 0 },
  { code: "PVM42", rate: null },
  { code: "PVM20", rate: 21 },
  { code: "PVM37", rate: 5 },
  { code: "PVM54", rate: 9 },
  { code: "PVM38", rate: 0 },
  { code: "PVM39", rate: null },
  { code: "PVM43", rate: 21 },
  { code: "PVM44", rate: 9 },
  { code: "PVM45", rate: 5 },
  { code: "PVM60", rate: 12 },
  { code: "PVM46", rate: 0 },
  { code: "PVM47", rate: null },
  { code: "PVM48", rate: null },
  { code: "PVM32", rate: 21 },
  { code: "PVM52", rate: 9 },
  { code: "PVM53", rate: 5 },
  { code: "PVM33", rate: 0 },
  { code: "PVM49", rate: 6 },
  { code: "PVM51", rate: 0 },
  { code: "PVM55", rate: 0 },
  { code: "PVM56", rate: null },
  { code: "PVM23", rate: null },
  { code: "PVM24", rate: null },
  { code: "PVM100", rate: null },
];

// Reasons the backend gives when it picked a code but is not sure of it — the
// accountant should look before the document goes to the accounting system.
// Shown in amber; every other reason (and a manual choice) is plain grey.
export const UNCERTAIN_PVM_REASONS: Set<string> = new Set([
  "lt_rate_no_vat_code",
  "lt_zero_rated",
  "lt_outside_scope",
  "lt_not_vat_payer",
  "eu_supply_kind_unknown",
  "eu_goods_services_conflict",
  "eu_exempt_services",
  "eu_lt_rate_vat_charged",
  "eu_outside_scope",
  "foreign_supply_kind_unknown",
  "foreign_exempt_services",
  "foreign_outside_scope",
  "supplier_origin_unknown",
  "unmapped",
  "rate_unknown",
  "foreign_lt_rate_vat_charged",
  "eu_zero_rated_services",
  "foreign_zero_rated_services",
  "supplier_learned_mismatch",
  "model_suggested",
  "model_disagrees",
]);

// Reasons that come from the document reader's own proposal: its one-line
// "why" is worth showing in the text, not only in the tooltip.
export const MODEL_PVM_REASONS: Set<string> = new Set(["model_suggested", "model_disagrees"]);

export const SUPPLY_KINDS = ["goods", "services", "mixed"] as const;

type Translate = (key: string, options?: any) => string;

export function pvmCodeLabel(t: Translate, code: string): string {
  return `${code} — ${t(`pvm_code_${code}`, { defaultValue: code })}`;
}

export function pvmReasonLabel(
  t: Translate,
  line: Pick<PurchaseVatLine, "pvmClassifierSource" | "pvmClassifierReason" | "modelPvmClassifier">
): string {
  if (line.pvmClassifierSource === "manual") {
    return t("invoice_inbox_pvm_reason_manual", { defaultValue: "Set manually" });
  }
  if (!line.pvmClassifierReason) return "";
  // A reason this build does not know yet still shows as its key rather than
  // disappearing — a new backend rule should be visible, if untranslated.
  const key = `invoice_inbox_pvm_reason_${line.pvmClassifierReason}`;
  // `code` is the reader's proposal — used by model_disagrees, ignored by the rest.
  return t(key, { defaultValue: key, code: line.modelPvmClassifier || "—" });
}

export function isUncertainPvmLine(
  line: Pick<PurchaseVatLine, "pvmClassifierSource" | "pvmClassifierReason">
): boolean {
  return (
    line.pvmClassifierSource !== "manual" &&
    Boolean(line.pvmClassifierReason) &&
    UNCERTAIN_PVM_REASONS.has(line.pvmClassifierReason as string)
  );
}
